import React from 'react';
import { SimpleButton, ErrorMessage } from '@/src/ui';

type Mode = 'new' | 'edit';

interface LineEditorPopupProps {
    left: number;
    top: number;
    width: number;
    labelDraft: string;
    setLabelDraft: (v: string) => void;
    styleDraft: 'segment' | 'ray' | 'line';
    setStyleDraft: (v: 'segment' | 'ray' | 'line') => void;
    allowedItemTypes: string[];
    allowedLabels: string[];
    mode: Mode;
    onSave: () => void;
    onCancel: () => void;
    onRemove?: () => void;
}

const lineTypeOptions = [
    { id: 'segment', value: 'Segment' },
    { id: 'ray', value: 'Ray' },
    { id: 'line', value: 'Line' },
];

export default function LineEditorPopup({
    left,
    top,
    width,
    labelDraft,
    setLabelDraft,
    styleDraft,
    setStyleDraft,
    allowedItemTypes,
    allowedLabels,
    mode,
    onSave,
    onCancel,
    onRemove,
}: LineEditorPopupProps) {
    const stopAll = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
    };

    return (
        <div
            style={{
                position: 'absolute',
                left,
                top,
                width,
                zIndex: 10,
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 4,
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                padding: '8px 12px',
                minWidth: 200,
                fontSize: 12,
                lineHeight: 1.4,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={stopAll}
            onContextMenu={stopAll}
        >
            <div className="flex flex-col gap-2">
                <div className="flex flex-col">
                    <label className="text-xs font-semibold mb-1">Label</label>
                    <select
                        className={`border border-gray-300 rounded px-2 py-1 text-sm`}
                        value={labelDraft}
                        onChange={(e) => setLabelDraft(e.target.value)}
                    >
                        {!labelDraft && <option value="">Select label</option>}
                        {allowedLabels.map((lbl) => (
                            <option key={lbl} value={lbl}>
                                {lbl}
                            </option>
                        ))}
                    </select>
                    {allowedLabels.length === 0 && (
                        <ErrorMessage error={{ message: 'No available labels for this line.' }} />
                    )}
                </div>

                <div className="flex flex-col">
                    <label className="text-xs font-semibold mb-1">Type</label>
                    <select
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        value={styleDraft}
                        onChange={(e) => setStyleDraft(e.target.value as any)}
                    >
                        {lineTypeOptions
                            .filter((opt) => {
                                return allowedItemTypes.includes(opt.id);
                            })
                            .map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.value}
                                </option>
                            ))}
                    </select>
                </div>

                <div className="flex flex-row gap-2">
                    {mode === 'edit' && onRemove && (
                        <SimpleButton
                            type="button"
                            color="error"
                            variant="outlined"
                            className="text-[11px] px-2 py-1"
                            onClick={(e) => {
                                stopAll(e);
                                onRemove();
                            }}
                        >
                            Remove
                        </SimpleButton>
                    )}

                    <SimpleButton
                        type="button"
                        color="primary"
                        className="bg-blue-600 text-white text-[11px] px-2 py-1 rounded"
                        onClick={(e) => {
                            stopAll(e);
                            onSave();
                        }}
                        disabled={!labelDraft || allowedLabels.length === 0}
                    >
                        Save
                    </SimpleButton>

                    <SimpleButton
                        type="button"
                        color="secondary"
                        variant="outlined"
                        className="text-[11px] px-2 py-1"
                        onClick={(e) => {
                            stopAll(e);
                            onCancel();
                        }}
                    >
                        Cancel
                    </SimpleButton>
                </div>
            </div>
        </div>
    );
}
